
import { ArvGalleryIcon } from '../../assets/arv/ArvGalleryIcon'
import { ExcludeIcon } from '../../assets/arv/ExcludeIcon'
import { ArvQualifiedIcon } from '../../assets/arv/ArvQualifiedIcon'
import { RestoreIcon } from '../../assets/RestoreIcon'
import { Link } from 'react-router-dom'
function formatPrice(n) {
  if (!n) return ''
  return '$' + Number(n).toLocaleString()
}

function monthsAgo(dateStr) {
  if (!dateStr) return null
  const diff = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  return diff.toFixed(1) + ' mo'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Style tokens ─────────────────────────────────────────────────────────────

const headerCls = { fontWeight: 700, fontSize: 14, color: '#17270D' }

// ─── Icons ───────────────────────────────────────────────────────────────────


// ─── Column definitions ───────────────────────────────────────────────────────
//
// ARV Comp Grid — style guide
//
// Header row:  bg #E7F0E2 · border-bottom #DAE4D5 · h-[52px]
//              text: 13px / 600 / #17270D
//
// Data rows:   h-[72px] · border-bottom #F0F5EE · hover bg #F8FEF4
//
// Score        70px  centered   bold 14px #4CBC18
// Address     300px  left       13px medium #4CBC18 underline (click → listing)
//                               sub: 11px #A0AFA9  beds·baths·sqft·yb
// Date        100px  left       12px #17270D  (months ago)
//                               sub: 11px #A0AFA9  (formatted date)
// Distance    100px  centered   13px #17270D  (x.xx mi)
// Status       75px  centered   badge pill: Sold → #4CBC18 bg · Active → #C13A4A bg
// Price       150px  right      13px medium #17270D  (list price)
//                               sub: 11px #A0AFA9  Adj: $xxx,xxx
// Actions      90px  centered   GalleryIcon (→ gallery modal) · UnqualifyIcon/QualifyIcon (toggle archived)
//                               UnqualifyIcon (active comp, opacity 0.35) → QualifyIcon (archived comp, opacity 1)

export function getArvColumns({ onRowClick, onToggleArchived, onGalleryOpen }) {
  return [
    // ── Score ────────────────────────────────────────────────────────────────
    {
      field: 'score',
      headerName: 'Score',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <span style={headerCls}>Score</span>,
      renderCell: ({ value }) => (
        <span 
          className="font-bold text-[14px]" 
          style={{ color: 
            value > 69 
              ? '#43B017' 
              : value > 59
                ? '#7EBA56'
                : '#17270D' 
          }}
        >
          {value ?? ''}
        </span>
      ),
    },

    // ── Property Address ─────────────────────────────────────────────────────
    {
      field: 'address',
      headerName: 'Address',
      width: 320,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Address</span>,
      renderCell: ({ row }) => {
        const { street, city, state, zip } = row.address ?? {}
        const citystatezip = [city, state, zip].filter(Boolean).join(', ')
        const { beds, baths, sqft } = row.specs ?? {}
        const sub = [
          beds   ? `${beds} bd`                        : null,
          baths  ? `${baths} ba`                       : null,
          sqft   ? `${Number(sqft).toLocaleString()} sqft` : null,
          row.year_built ? `${row.year_built} yb`      : null,
        ].filter(Boolean).join(' · ')
        return (
          <div
            className="flex flex-col justify-center gap-y-[2px] cursor-pointer py-[16px] w-[100%] group"
            onClick={() => onRowClick(row)}
          >
            <span className="text-[14px] font-semibold group-hover:text-brand-green leading-tight">
              {street } <span className="text-[13px] font-[400] text-brand-secondary group-hover:text-brand-green">{citystatezip}</span>
            </span>
            <span className="text-[13px] text-brand-secondary leading-tight">{sub}</span>
          </div>
        )
      },
    },

    // ── Date ─────────────────────────────────────────────────────────────────
    {
      field: 'last_sold_date',
      headerName: 'Date',
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Date</span>,
      renderCell: ({ row }) => {
        const dateStr = row.status === 'Sold' ? row.last_sold_date : row.list_date
        return (
          <div
            className="flex flex-col justify-center gap-y-[2px] py-[4px]"
          >
            <span className="text-[14px] font-semibold text-brand-dark font-bold leading-tight">{monthsAgo(dateStr)}</span>
            <span className="text-[13px] text-brand-secondary leading-tight">{formatDate(dateStr)}</span>
          </div>
        )
      },
    },

    // ── Distance ─────────────────────────────────────────────────────────────
    {
      field: 'distanceSubj',
      headerName: 'Distance',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Distance</span>,
      renderCell: ({ value }) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#17270D' }}>
          {value != null ? `${value.toFixed(2)}` : ''}
        </span>
      ),
    },

    // ── Status ───────────────────────────────────────────────────────────────
    {
      field: 'status',
      headerName: 'Status',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Status</span>,
      renderCell: ({ value }) => {
        return (
          <span
            className="flex h-[18px] px-[6px] py-[2px] rounded"
            style={{ 
              background: value === 'Sold' ? '#4CBC18' : value === 'Active' ? '#C13A4A' : '#F5C312'
            }}
          >
            <span className="text-[11px] flex text-white items-center">
              {value?.[0]}
            </span>
          </span>
        )
      },
    },

    // ── Price ────────────────────────────────────────────────────────────────
    {
      field: 'price',
      headerName: 'Price',
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => <span style={headerCls}>Price</span>,
      renderCell: ({ row }) => (
        <div className="flex flex-col items-end justify-center gap-y-[2px] py-[4px]">
          <span className="text-[14px] font-semibold text-brand-dark leading-tight">
            {row.status !== 'Sold' ? formatPrice(row.price) : formatPrice(row.sold_price)}
          </span>
          <span className="text-[13px] text-brand-secondary leading-tight">
              Adjusted: {formatPrice(row.adjustments)}
            </span>
        </div>
      ),
    },

    // ── Actions ──────────────────────────────────────────────────────────────
    {
      field: '__actions',
      headerName: '',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: ({ row }) => (
        <div className="flex items-center justify-center gap-x-[14px] w-full">
          <button
            onClick={(e) => { e.stopPropagation(); onGalleryOpen?.(row.photos ?? []) }}
            className="flex items-center text-[#4F5F4A] hover:text-[#3AA015] transition-colors"
          >
            <ArvGalleryIcon />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleArchived?.(row) }}
            className={`flex items-center text-[#4F5F4A] hover:text-[#4CBC18] transition-colors ${row.archived ? 'hover:text-[#4CBC18]': 'hover:text-[#D85263]'}`}
          >
            {row.archived ? <RestoreIcon /> : <ExcludeIcon />}
          </button>
        </div>
      ),
    },
  ]
}

export function getVerifyColumns() {
  return [
    // ── Address ──────────────────────────────────────────────────────────────
    {
      field: 'address',
      headerName: 'Address',
      width: 300,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Address</span>,
      renderCell: ({ row }) => {
        const { street, city, state, zip } = row.address ?? {}
        const addr = [street, city, state, zip].filter(Boolean).join(', ')
        return (
          <span style={{ fontSize: 14, fontWeight: 500, color: '#17270D' }}>{addr}</span>
        )
      },
    },

    // ── Status ───────────────────────────────────────────────────────────────
    {
      field: 'status',
      headerName: 'Status',
      width: 75,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Status</span>,
      renderCell: ({ value }) => (
        <span
          className="flex h-[18px] px-[6px] py-[2px] rounded"
          style={{
            background: value === 'Sold' ? '#4CBC18' : value === 'Active' ? '#C13A4A' : '#F5C312',
          }}
        >
          <span className="text-[11px] flex text-white items-center">{value?.[0]}</span>
        </span>
      ),
    },

    // ── Distance ─────────────────────────────────────────────────────────────
    {
      field: 'distanceSubj',
      headerName: 'Distance',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Distance</span>,
      renderCell: ({ value }) => (
        <span style={{ fontSize: 14, fontWeight: 500, color: '#17270D' }}>
          {value != null ? `${value.toFixed(2)} mi` : ''}
        </span>
      ),
    },

    // ── Score ────────────────────────────────────────────────────────────────
    {
      field: 'score',
      headerName: 'Score',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Score</span>,
      renderCell: ({ value }) => (
        <span
          className="font-bold text-[14px]"
          style={{
            color: value > 69 ? '#43B017' : value > 59 ? '#7EBA56' : '#17270D',
          }}
        >
          {value ?? ''}
        </span>
      ),
    },

    // ── Date ─────────────────────────────────────────────────────────────────
    {
      field: '__date',
      headerName: 'Date',
      width: 110,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Date</span>,
      renderCell: ({ row }) => {
        const dateStr = row.status === 'Sold' ? row.last_sold_date : row.list_date
        return (
          <span style={{ fontSize: 14, fontWeight: 400, color: '#17270D' }}>
            {formatDate(dateStr)}
          </span>
        )
      },
    },
    {
      field: 'linkk',
      headerName: '',
      width: 250,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
      <span
      >
        <a target='_blank' href={`${params.value}`}>
          {params.value}
        </a>
      </span>
    ),
    },
    {
      field: 'price',
      headerName: 'price',
      width: 125,
      sortable: false,
      disableColumnMenu: true,
      
    },
    {
      field: 'sold_price',
      headerName: 'sol.pr',
      width: 125,
      sortable: false,
      disableColumnMenu: true,
      
    }
  ]
}
